// 📄 components/StepReceiptOutput.tsx
import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { ReceiptFormData } from '../../../../../services/commerce/integrationService';

interface StepReceiptOutputProps {
  outputTypeGR: string;
  outputProgram: string;
  outputRoutine: string;
  specialFunction: string;
  onChange: (field: keyof ReceiptFormData, value: any) => void;
  isInvalid: boolean;
}

export function StepReceiptOutput({ outputTypeGR, outputProgram, outputRoutine, specialFunction, onChange, isInvalid }: StepReceiptOutputProps) {
  return (
    <div className="space-y-4 animate-fadeIn">
      <div className="bg-blue-500/5 border border-blue-500/10 rounded-xl p-3 mb-2 space-y-1">
        <span className="text-[10px] font-bold text-blue-400 uppercase tracking-widest block">Ruta SPRO / Transacción SAP</span>
        <p className="font-mono text-xs text-gray-300">
          <span className="bg-zinc-800 px-1.5 py-0.5 rounded text-gray-400 font-bold mr-1.5">NACE</span>
          <span>Materials Management → Inventory Management and Physical Inventory → Output Determination → Maintain Output Types for Inventory Management</span>
        </p>
      </div>

      <div>
        <h3 className="text-lg font-medium text-blue-400">1. Define Message Output Control (Aplicación ME)</h3>
        <p className="text-sm text-gray-400">
          Configura el output type que dispara el envío de la nota de recepción (Goods Receipt Note) hacia SAP Ariba cuando se registra el MIGO.
        </p>
      </div>

      <div className="bg-white/5 p-4 rounded-xl border border-white/5 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-medium text-gray-400 mb-1">
            Output Type (Aplicación ME) <span className="text-red-400">*</span>
          </label>
          <input
            type="text"
            value={outputTypeGR}
            onChange={(e) => onChange('outputTypeGR', e.target.value.toUpperCase())}
            placeholder="ej. ZWE3"
            maxLength={4}
            className={`w-full bg-zinc-800 border rounded-lg p-2.5 text-sm font-mono text-white focus:outline-none focus:border-blue-500 ${
              isInvalid && !outputTypeGR ? 'border-red-500' : 'border-white/10'
            }`}
          />
          <p className="text-[10px] text-gray-500 mt-1">Ariba GR Note V3</p>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-400 mb-1">Program</label>
          <input
            type="text"
            value={outputProgram}
            onChange={(e) => onChange('outputProgram', e.target.value.toUpperCase())}
            placeholder="SAPM07DR"
            className="w-full bg-zinc-800 border border-white/10 rounded-lg p-2.5 text-sm font-mono text-white focus:outline-none focus:border-blue-500"
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-400 mb-1">Routine (FORM Rutina)</label>
          <input
            type="text"
            value={outputRoutine}
            onChange={(e) => onChange('outputRoutine', e.target.value.toUpperCase())}
            placeholder="ENTRY_WE03"
            className="w-full bg-zinc-800 border border-white/10 rounded-lg p-2.5 text-sm font-mono text-white focus:outline-none focus:border-blue-500"
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-400 mb-1">Special Function</label>
          <input
            type="text"
            value={specialFunction}
            onChange={(e) => onChange('specialFunction', e.target.value.toUpperCase())}
            placeholder="ARBCIG_RECEIPT_PUBLISH_SEND_GOODSRECEIPT_SA"
            className="w-full bg-zinc-800 border border-white/10 rounded-lg p-2.5 text-sm font-mono text-white focus:outline-none focus:border-blue-500"
          />
        </div>
      </div>

      <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-xl p-4 text-yellow-400 flex gap-3 text-sm">
        <AlertTriangle className="w-5 h-5 flex-shrink-0" />
        <div>
          <p className="font-semibold">Condición de Salida</p>
          <p className="text-xs text-yellow-500/80 mt-0.5">
            Verifica que la condición de acceso (Access Sequence) del output type incluya el tipo de movimiento y el centro (Plant) correctos para que se dispare únicamente en las recepciones relevantes.
          </p>
        </div>
      </div>
    </div>
  );
}
